import { Label } from "layout/typography/Label";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useState } from "react";
import { useLazyResolveProcedureCopayListQuery } from "store/queries/procedureCopayList";
import { IProcedureCopayListResolution } from "types/ProcedureCopayList/ProcedureCopayList";

import {
  centsInDollars,
  codeStatusInWords,
  proceduresFromText,
  resolveReasonInWords,
} from "./copayListWording";

/** The most procedures the API resolves in one call (`PROCEDURE_COPAY_LIST_RESOLVE_MAX`). */
export const RESOLVE_PROCEDURES_MAX = 50;

interface ICopayListTesterProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * CLE-2816 PR 2 — "Test codes", a dialog opened from the tab's button row: type an estimate's
 * procedures (a code per line, optionally with its facility charge) and see which item the engine
 * would read first, and why — through the resolve endpoint, the same loader and resolver as a
 * real eligibility check, so it reads the list as saved. Nothing is stored.
 */
export function CopayListTester({ visible, onHide }: ICopayListTesterProps) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<IProcedureCopayListResolution | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [resolve, { isFetching }] = useLazyResolveProcedureCopayListQuery();

  const { procedures, problem } = proceduresFromText(text);
  const tooMany = procedures.length > RESOLVE_PROCEDURES_MAX;

  const run = async () => {
    if (procedures.length === 0 || tooMany || problem) return;
    setError(null);
    try {
      setResult(await resolve(procedures).unwrap());
    } catch (caught: any) {
      setResult(null);
      setError(
        caught?.data?.message ||
          caught?.message ||
          "The codes could not be tested."
      );
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Test codes"
      style={{ width: "min(620px, 95vw)" }}
      data-testid="copayList.testerDialog"
    >
      <Surface data-testid="copayList.tester">
        <div className="flex flex-column gap-1 mb-3">
          <Label htmlFor="copayList.tester.procedures">
            The estimate’s procedures
          </Label>
          <InputTextarea
            id="copayList.tester.procedures"
            data-testid="copayList.tester.procedures"
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={"42830 1750\n69436 2000"}
          />
          <small className="text-color-secondary">
            One per line: the code, then optionally its facility charge in
            dollars. Without charges, the first mapped code decides. At most{" "}
            {RESOLVE_PROCEDURES_MAX}.
          </small>
        </div>

        <Button
          type="button"
          label="Test"
          icon="pi pi-play"
          className="p-button-sm mb-3"
          loading={isFetching}
          disabled={procedures.length === 0 || tooMany || !!problem}
          onClick={run}
          data-testid="copayList.tester.run"
        />

        {problem && <Message severity="warn" text={problem} />}
        {tooMany && (
          <Message
            severity="warn"
            text={`At most ${RESOLVE_PROCEDURES_MAX} procedures at a time.`}
          />
        )}
        {error && <Message severity="error" text={error} />}

        {result && (
          <div
            className="flex flex-column gap-2"
            data-testid="copayList.tester.result"
          >
            <span
              className="font-semibold"
              data-testid="copayList.tester.reason"
            >
              {resolveReasonInWords(result)}
            </span>
            {result.items.length > 1 && (
              <small
                className="text-color-secondary"
                data-testid="copayList.tester.order"
              >
                Order read: {result.items.join(" → ")}. If the payer’s list has
                no line for an item, the next is read, then OUTPATIENT HOSPITAL
                CARE.
              </small>
            )}
            {result.builtIn && (
              <small
                className="text-color-secondary"
                data-testid="copayList.tester.builtIn"
              >
                Nothing is saved yet, so this used the built-in list.
              </small>
            )}
            {result.codes.map((answer) => (
              <div
                key={answer.code}
                className="flex gap-2"
                data-testid={`copayList.tester.code.${answer.code}`}
              >
                <span className="font-mono" style={{ minWidth: "6rem" }}>
                  {answer.code}
                </span>
                <span
                  className={
                    answer.status === "MAPPED"
                      ? "font-semibold"
                      : "text-color-secondary"
                  }
                >
                  {codeStatusInWords(answer.status, answer.itemName)}
                </span>
                {answer.facilityCents !== null && (
                  <span className="ml-auto">
                    {centsInDollars(answer.facilityCents)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Surface>
    </Dialog>
  );
}
